/**
 * Session state (PLAN.md §9) and the item model a session carries.
 *
 * In-memory only. AGENTS.md forbids session data touching IndexedDB, the Cache
 * API or localStorage, so nothing here is ever persisted — the store dies with
 * the tab, which is the intended lifetime (PLAN.md §1).
 */

import { create } from 'zustand'
import type { SessionRole } from '../lib/signaling'

export type { SessionRole }

// ---------------------------------------------------------------------------
// Item model (PLAN.md §9)
// ---------------------------------------------------------------------------

export type ItemType = 'text' | 'richtext' | 'image' | 'file' | 'locked'
export type ItemStatus = 'pending' | 'transferring' | 'complete' | 'error'

export interface BaseItem {
  /** uuid v4, generated by the sender. */
  id: string
  type: ItemType
  status: ItemStatus
  createdAt: number
}

export interface TextItem extends BaseItem {
  type: 'text'
  /** Streams live as the sender types. */
  content: string
}

export interface RichTextItem extends BaseItem {
  type: 'richtext'
  /** Tiptap JSON. */
  content: string
}

export interface ImageItem extends BaseItem {
  type: 'image'
  fileName: string
  mimeType: string
  totalSize: number
  totalChunks: number
  /** 0–100. */
  progress: number
  /**
   * The sender's copied source `File`, or the Blob assembled from chunks on the
   * receiver. The transport never sets `objectURL`: components own the URLs they
   * create (and revoke) from this Blob.
   */
  blob?: Blob
  objectURL?: string
}

export interface FileItem extends BaseItem {
  type: 'file'
  fileName: string
  mimeType: string
  totalSize: number
  totalChunks: number
  /** 0–100. */
  progress: number
  /** The sender's copied source `File`, or the Blob assembled from chunks on the receiver. */
  blob?: Blob
}

export interface LockedItem extends BaseItem {
  type: 'locked'
  /** Plaintext, visible without the password. */
  label: string
  innerType: 'text' | 'richtext' | 'file'
  ciphertext: Uint8Array
  iv: Uint8Array
  salt: Uint8Array
  // Receiver-side only — never sent.
  unlocked?: boolean
  plaintextContent?: string | Blob
}

export type SessionItem = TextItem | RichTextItem | ImageItem | FileItem | LockedItem

// ---------------------------------------------------------------------------
// Session state (PLAN.md §9)
// ---------------------------------------------------------------------------

export type SessionPhase = 'idle' | 'connecting' | 'pairing' | 'verified' | 'active' | 'ended'

export interface SessionState {
  role: SessionRole | null
  phase: SessionPhase
  sessionCode: string | null
  safetyPhrase: [string, string, string] | null
  phraseConfirmed: boolean
  /**
   * ADDITIVE to PLAN.md §9 (Phase 2). True once the *peer's* phrase-confirm has
   * arrived over the (encrypted) data channel. Kept separate from
   * `phraseConfirmed`, which only ever means "this device tapped Confirmed",
   * because PLAN.md §8 blocks the session until both sides have confirmed.
   */
  peerConfirmed: boolean
  items: SessionItem[]
  connectionState: RTCPeerConnectionState
  /**
   * ADDITIVE to PLAN.md §9.
   *
   * `connectionState` can express a *WebRTC* failure but not a *signaling* one —
   * there is no RTCPeerConnectionState for "the worker returned 404 for this
   * code". PLAN.md §8 also requires an explicit "Error" status in Phase 1, and
   * neither `phase` (no 'error' member) nor `connectionState` can carry a
   * human-readable cause. Reported to the parent as a deliberate superset.
   */
  errorMessage: string | null
}

export const INITIAL_SESSION_STATE: SessionState = {
  role: null,
  phase: 'idle',
  sessionCode: null,
  safetyPhrase: null,
  phraseConfirmed: false,
  peerConfirmed: false,
  items: [],
  connectionState: 'new',
  errorMessage: null,
}

export interface SessionActions {
  /** Returns the store to its initial state. Leaves no trace of the last session. */
  reset: () => void
  /** Enters the connecting phase and fixes the role for this session. */
  startConnecting: (role: SessionRole, sessionCode: string | null) => void
  setSessionCode: (sessionCode: string | null) => void
  setPhase: (phase: SessionPhase) => void
  /** Phase 2 fills this once the ECDH-derived phrase is available. */
  setSafetyPhrase: (phrase: [string, string, string] | null) => void
  /** Phase 2 calls this when the user confirms the phrase matches. */
  setPhraseConfirmed: (confirmed: boolean) => void
  /**
   * Phase 2 records this device's confirmation. Narrower than
   * `setPhraseConfirmed`: confirmation is one-way, so there is no un-confirm
   * path and callers cannot accidentally clear it.
   */
  confirmPhrase: () => void
  /** Phase 2 sets this when the peer's phrase-confirm arrives (or is withdrawn). */
  setPeerConfirmed: (value: boolean) => void
  /**
   * Derived, not stored: PLAN.md §8 lets the session proceed only once both
   * sides have confirmed. Read it through the store so both flags are fresh —
   * `useSessionStore((state) => state.bothConfirmed())` in a component.
   */
  bothConfirmed: () => boolean
  /**
   * D14: Derived, not stored: only the sender gates the session on confirming the phrase.
   * On the sender ('guest'), true when this device has confirmed (phraseConfirmed).
   * On the receiver ('host'), true when the peer has confirmed (peerConfirmed).
   * If role is not yet determined, falls back to bothConfirmed().
   */
  isSenderConfirmed: () => boolean
  /** Phase 3 replaces the whole ordered board. */
  setItems: (items: SessionItem[]) => void
  /** Phase 3 updates one item in place as chunks and deltas arrive. */
  upsertItem: (item: SessionItem) => void
  /**
   * Phase 3 updates one item in place through a returns-the-same-item-or-a-new-one
   * function. Additive to PLAN.md §9, which does not describe the transport's
   * write path: a content delta, a throttled progress tick and a completed file's
   * `blob` are all "replace this item", and a typed patch object cannot express
   * that across the `SessionItem` union. A missing id is a no-op, so a delta that
   * raced ahead of its announce cannot resurrect an item nobody created.
   */
  updateItem: (id: string, update: (item: SessionItem) => SessionItem) => void
  /** Phase 3 removes one item (a local delete, or the peer's item-delete). */
  removeItem: (id: string) => void
  setConnectionState: (connectionState: RTCPeerConnectionState) => void
  /**
   * Ends the session. Pass a reason to mark it a failure — the UI then shows
   * "Error" with that message. Pass null for a clean end, which the UI shows as
   * "Session ended" (PLAN.md §8 Phase 4).
   *
   * One action rather than two: both paths produce the same state change and
   * differ only in whether a cause is attached.
   */
  endSession: (reason: string | null) => void
}

export type SessionStore = SessionState & SessionActions

export const useSessionStore = create<SessionStore>()((set, get) => ({
  ...INITIAL_SESSION_STATE,

  reset: () => {
    set({ ...INITIAL_SESSION_STATE })
  },

  startConnecting: (role, sessionCode) => {
    set({
      role,
      phase: 'connecting',
      sessionCode,
      safetyPhrase: null,
      phraseConfirmed: false,
      peerConfirmed: false,
      items: [],
      connectionState: 'new',
      errorMessage: null,
    })
  },

  setSessionCode: (sessionCode) => {
    set({ sessionCode })
  },

  setPhase: (phase) => {
    set({ phase })
  },

  setSafetyPhrase: (safetyPhrase) => {
    set({ safetyPhrase })
  },

  setPhraseConfirmed: (phraseConfirmed) => {
    set({ phraseConfirmed })
  },

  confirmPhrase: () => {
    set({ phraseConfirmed: true })
  },

  setPeerConfirmed: (peerConfirmed) => {
    set({ peerConfirmed })
  },

  bothConfirmed: () => {
    const { phraseConfirmed, peerConfirmed } = get()
    return phraseConfirmed && peerConfirmed
  },

  isSenderConfirmed: () => {
    const { role, phraseConfirmed, peerConfirmed } = get()
    if (role === 'guest') return phraseConfirmed
    if (role === 'host') return peerConfirmed
    return phraseConfirmed && peerConfirmed
  },

  setItems: (items) => {
    set({ items })
  },

  upsertItem: (item) => {
    set((state) => {
      const index = state.items.findIndex((existing) => existing.id === item.id)
      if (index === -1) {
        return { items: [...state.items, item] }
      }
      const items = state.items.slice()
      items[index] = item
      return { items }
    })
  },

  updateItem: (id, update) => {
    set((state) => {
      const index = state.items.findIndex((existing) => existing.id === id)
      if (index === -1) return state

      const current = state.items[index]
      if (current === undefined) return state

      const items = state.items.slice()
      items[index] = update(current)
      return { items }
    })
  },

  removeItem: (id) => {
    set((state) => {
      const items = state.items.filter((item) => item.id !== id)
      // Identity when nothing matched: a no-op must not re-render the board.
      if (items.length === state.items.length) return state
      return { items }
    })
  },

  setConnectionState: (connectionState) => {
    set({ connectionState })
  },

  endSession: (reason) => {
    // The peer flag is dropped with the session: a confirmation from a finished
    // session must never count towards the next one.
    set({ phase: 'ended', peerConfirmed: false, errorMessage: reason })
  },
}))
